import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import styles from './InspectionsPage.module.scss';

import LoadingSpinner from '../../components/LoadingSpinner';
import OrganizationCard from '../organizations/components/OrganizationCard';
import { useInspections } from '../../api/hooks/useInspections';
import { OrganizationDto } from '../../api/types/dtos';
import { InspectionDto } from '../../api/types/dtos/inspection';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useOrganizationDynamicProperties } from '../../api/hooks/useOrganizationDynamicProperties';
import { usePreloadDynamicPropertyTypes } from '../../api/hooks/useDynamicPropertyTypes';

export const InspectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useInspections();
  
  // Fetch organization and dynamic properties
  const { selectedOrganization } = useOrganization();
  const { data: dynamicPropertyTypes } = usePreloadDynamicPropertyTypes();
  const { data: organizationDynamicProperties } = useOrganizationDynamicProperties(
    selectedOrganization?.id || '',
    { enabled: !!selectedOrganization?.id } as any
  );

  // Helper function to check if inspections section should be visible
  const getInspectionsSectionVisibility = (): boolean => {
    // LEVEL 1: Try to find in organization-specific properties
    if (organizationDynamicProperties?.data) {
      const inspectionsProperty = organizationDynamicProperties.data.find(
        prop => prop.propertyTypeName === 'section_inspections'
      );
      
      if (inspectionsProperty?.value) {
        // Convert string '0'/'1' to boolean
        return inspectionsProperty.value === '1';
      }
    }
    
    // LEVEL 2: Fallback to default value from global property types
    if (dynamicPropertyTypes?.data) {
      const inspectionsType = dynamicPropertyTypes.data.find(
        type => type.name === 'section_inspections'
      );
      
      if (inspectionsType?.defaultValue) {
        // Convert string '0'/'1' to boolean
        return inspectionsType.defaultValue === '1';
      }
    }
    
    // LEVEL 3: Final hardcoded fallback - DENY ACCESS by default if property doesn't exist
    // This ensures that if the property type doesn't exist in the backend,
    // access is denied for security reasons (secure by default)
    return false;
  };

  // Get unique organizations and their latest inspection
  const organizationsWithInspections = data?.data?.reduce<{ org: OrganizationDto; inspection: InspectionDto }[]>((acc, inspection) => {
    if (!inspection.inspectedOrg) return acc;

    const existingIndex = acc.findIndex(item => item.org.id === inspection.inspectedOrg.id);
    if (existingIndex === -1) {
      acc.push({ org: inspection.inspectedOrg, inspection });
    } else {
      // If we already have this org, update if this inspection is more recent
      const existingDate = new Date(acc[existingIndex].inspection.startDate);
      const newDate = new Date(inspection.startDate);
      if (newDate > existingDate) {
        acc[existingIndex] = { org: inspection.inspectedOrg, inspection };
      }
    }
    return acc;
  }, []) || [];

  const handleOrganizationClick = (inspection?: InspectionDto) => {
    if (inspection?.id) {
      navigate(`/inspections/${inspection.id}/findings`);
    }
  };

  // Error handling
  if (error) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          Error cargando auditorías: {error.message}
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Check if inspections section should be visible
  if (!getInspectionsSectionVisibility()) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info" sx={{ mt: 4 }}>
          La sección de Auditorías no está disponible para tu organización.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <div className={styles.container}>
        <h1 className={styles.title}>
          Administra tus Auditorías
        </h1>

        <div className={styles.content}>
          {organizationsWithInspections.length === 0 ? (
            <div className={styles.emptyState}>
              <h2 className={styles.emptyStateText}>
                No se encontraron auditorías.
              </h2>
            </div>
          ) : (
            <Grid container spacing={3} className={styles.grid}>
              {organizationsWithInspections.map(({ org, inspection }) => (
                <Grid item xs={12} sm={6} md={3} key={org.id} className={styles.gridItem}>
                  <OrganizationCard 
                    organization={org}
                    onClick={handleOrganizationClick}
                    inspection={inspection}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      </div>
    </Container>
  );
};

export default InspectionsPage; 